import React, { useState, useCallback } from 'react';
import { apiService, PostData } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePaginatedData } from './useDataFetch';
import { parseApiError } from '@/utils/apiErrorHandler';

interface UsePostsOptions {
  limit?: number;
  initialOffset?: number;
  autoFetch?: boolean;
  onlyHighlights?: string;
}

interface UsePostsReturn {
  posts: PostData[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  addPost: (post: PostData) => void;
  updatePost: (postId: string, updates: Partial<PostData>) => void;
  removePost: (postId: string) => void;
}

export const usePosts = (options: UsePostsOptions = {}): UsePostsReturn => {
  const {
    limit = 10,
    initialOffset = 0,
    autoFetch = true,
    onlyHighlights
  } = options;

  // Use the correct properties from AuthContext
  const { currentUser, authToken, isAuthenticated, isLoading: authLoading } = useAuth();
  const [posts, setPosts] = useState<PostData[]>([]);

  // Create a stable fetch function that uses current onlyHighlights value
  const fetchFunction = useCallback(async (params: { limit: number; offset: number }, token?: string) => {
    // Check for authentication properly
    if (!isAuthenticated || !currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      // Use the correct API method signature and handle the response properly.
      // Passing `userID` lets the backend include private + community joydrops
      // that this user can see, instead of falling back to the public-only feed.
      const response = await apiService.getPosts(
        { 
          limit: params.limit, 
          offset: params.offset,
          onlyHighlights: onlyHighlights || "",
          userID: currentUser.uid
        },
        token || authToken
      );

      console.log("Raw API response:", response);

      // Handle the response structure based on your API
      // Your API returns: ApiResponse<{ posts: PostData[], hasMore: boolean, total: number }>
      // So the structure is: { statusCode, status, message, data: { posts, hasMore, total } }
      
      let postsData: PostData[] = [];
      let hasMore = false;
      let total = 0;

      if (response && typeof response === 'object') {
        // Check if it's wrapped in ApiResponse format
        if ('data' in response && response.data) {
          postsData = Array.isArray(response.data.posts) ? response.data.posts : [];
          hasMore = Boolean(response.data.hasMore);
          total = response.data.total || 0;
        }
        // Check if it's the direct format (posts, hasMore, total at root level)
        else if ('posts' in response) {
          postsData = Array.isArray(response.posts) ? response.posts : [];
          hasMore = Boolean(response.hasMore);
          total = response.total || 0;
        }
        // Fallback: if response is directly an array of posts
        else if (Array.isArray(response)) {
          postsData = response;
          hasMore = false;
          total = response.length;
        }
      }

      console.log("Processed posts data:", {
        postsCount: postsData.length,
        hasMore,
        total
      });

      return {
        data: postsData,
        hasMore,
        total,
      };
    } catch (error) {
      const parsed = parseApiError(error);
      if (parsed.type === 'network') {
        console.debug('Posts fetch: API unreachable');
      } else {
        console.error('Error in fetchFunction:', error);
      }
      throw error;
    }
  }, [currentUser?.uid, onlyHighlights, isAuthenticated, authToken]);

  // Use centralized paginated data fetching
  const {
    data: fetchedPosts,
    loading,
    error,
    hasMore,
    loadMore: loadMorePosts,
    refresh: refreshPosts,
  } = usePaginatedData<PostData>({
    fetchFunction,
    initialLimit: limit,
    enabled: autoFetch && isAuthenticated && !!currentUser?.uid && !authLoading,
    dependencies: [onlyHighlights], // Re-fetch when onlyHighlights changes
  });

  // Update local posts state when fetched data changes
  React.useEffect(() => {
    console.log("fetchedPosts changed:", fetchedPosts?.length || 0, "posts");
    if (fetchedPosts && Array.isArray(fetchedPosts)) {
      setPosts(fetchedPosts);
    } else {
      setPosts([]);
    }
  }, [fetchedPosts]);

  // Optimized post management functions
  const addPost = useCallback((post: PostData) => {
    setPosts(prev => [post, ...(Array.isArray(prev) ? prev : [])]);
  }, []);

  const updatePost = useCallback((postId: string, updates: Partial<PostData>) => {
    setPosts(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      return safePrev.map(post => 
        post.id === postId ? { ...post, ...updates } : post
      );
    });
  }, []);

  const removePost = useCallback((postId: string) => {
    setPosts(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      return safePrev.filter(post => post.id !== postId);
    });
  }, []);

  return {
    posts,
    loading,
    error,
    hasMore,
    loadMore: loadMorePosts,
    refresh: refreshPosts,
    addPost,
    updatePost,
    removePost
  };
};
